import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Row } from 'react-bootstrap';
import DataLoadError from '@/components/errors/DataLoadError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import CancelModal from '@/app/common/components/CancelModal';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { BrandFavicon } from '../components/BrandFavicon';
import { CompetitorModal } from '../components/Modals';
import { CompetitorsPageSkeleton } from '../components/common/Skeleton';
import { KpiStatCard } from '../components/common/KpiStatCard';
import { NoProject } from '../components/NoProject';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../utils/help/help-content';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import { CompetitorSourceGapTable } from '../components/sources/CompetitorSourceGapTable';
import {
  getCompetitorRankingColumns,
  getProviderComparisonColumns,
  getPromptCompetitionColumns,
  type CompetitorRankingRow,
  type ProviderComparisonBrandRow
} from '../config/competitorColumns';
import { useProject } from '../context/ProjectContext';
import { useTrackerAiPath } from '../utils/paths';
import {
  intelligenceRowMatchesCompetitor,
  isTrackedCompetitorAppearing
} from '../utils/brand-matching';
import { buildDateRangeQueryOptions } from '../utils/visibility-range';
import { normalizeHost } from '../utils/favicon';
import type {
  CompetitorInsight,
  CompetitorIntelligence,
  CompetitorIntelligenceRow,
  UntrackedPerformerRow
} from '../types';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function CompetitorsPage() {
  return (
    <NoProject>
      <CompetitorsContent />
    </NoProject>
  );
}

function CompetitorsContent() {
  const {
    projectId,
    topicFilterId,
    modelFilterId,
    visibilityRange,
    customFrom,
    customTo,
    domainFilterId,
    sourceFilterIds
  } = useProject();
  const navigate = useNavigate();
  const toTrackerAi = useTrackerAiPath();
  const rangeQuery = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        topicId: topicFilterId ?? undefined,
        providerIds: modelFilterId ? [modelFilterId] : undefined,
        sourceIds: sourceFilterIds.length > 0 ? sourceFilterIds : undefined
      }),
    [visibilityRange, customFrom, customTo, topicFilterId, modelFilterId, sourceFilterIds]
  );
  const sourceGapRangeQuery = rangeQuery;
  const [intelligence, setIntelligence] = useState<CompetitorIntelligence | null>(null);
  const [managedCompetitors, setManagedCompetitors] = useState<CompetitorInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CompetitorInsight | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState<CompetitorInsight | null>(null);
  const [draftCompetitor, setDraftCompetitor] = useState<{ brandName: string } | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [trackingBrandKey, setTrackingBrandKey] = useState<string | null>(null);
  const [showAllUntrackedPerformers, setShowAllUntrackedPerformers] = useState(false);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    const [intel, list] = await Promise.all([
      api.getCompetitorIntelligence(projectId, rangeQuery),
      api.getCompetitors(projectId)
    ]);
    setIntelligence(intel as CompetitorIntelligence);
    setManagedCompetitors(list as CompetitorInsight[]);
  }, [projectId, rangeQuery]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    refresh()
      .then(() => {
        if (!cancelled) setLoadFailed(false);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, refresh, retryNonce]);

  const appearingCompetitorRows = intelligence?.competitors ?? [];
  const notAppearingCompetitors = useMemo(
    () =>
      managedCompetitors.filter(
        competitor => !isTrackedCompetitorAppearing(competitor, appearingCompetitorRows)
      ),
    [managedCompetitors, appearingCompetitorRows]
  );

  const untrackedPerformers = useMemo(() => {
    const rows = intelligence?.untrackedPerformers ?? [];
    return rows.filter(
      row =>
        !managedCompetitors.some(competitor =>
          intelligenceRowMatchesCompetitor(
            { brandId: row.brandId, name: row.name, domain: row.domain },
            competitor
          )
        )
    );
  }, [intelligence, managedCompetitors]);

  const untrackedPerformerKeys = useMemo(
    () => untrackedPerformers.map(row => row.brandId ?? row.name).join('\0'),
    [untrackedPerformers]
  );

  useEffect(() => {
    setShowAllUntrackedPerformers(false);
  }, [untrackedPerformerKeys]);

  const visibleUntrackedPerformers = showAllUntrackedPerformers
    ? untrackedPerformers
    : untrackedPerformers.slice(0, 5);
  const hiddenUntrackedCount = Math.max(0, untrackedPerformers.length - 5);

  const providerBrandNames: ProviderComparisonBrandRow[] = useMemo(() => {
    const byName = new Map<string, ProviderComparisonBrandRow>();
    for (const row of intelligence?.competitors ?? []) {
      byName.set(row.name, {
        name: row.name,
        domain: row.domain,
        faviconUrl: row.faviconUrl
      });
    }
    for (const row of intelligence?.providerComparison ?? []) {
      for (const cell of row.competitors) {
        if (!byName.has(cell.brand)) {
          byName.set(cell.brand, {
            name: cell.brand,
            domain: null,
            faviconUrl: null
          });
        }
      }
    }
    return [...byName.values()];
  }, [intelligence]);

  const promptCompetitorColumns = useMemo(() => {
    const names: string[] = [];
    const seen = new Set<string>();
    for (const row of intelligence?.competitors ?? []) {
      if (!seen.has(row.name)) {
        seen.add(row.name);
        names.push(row.name);
      }
    }
    for (const row of intelligence?.promptCompetition ?? []) {
      for (const competitor of row.competitors) {
        if (!seen.has(competitor.brand)) {
          seen.add(competitor.brand);
          names.push(competitor.brand);
        }
      }
    }
    return names.slice(0, 6);
  }, [intelligence]);

  async function handleSave(data: { brandName: string; websiteUrl: string; aliases: string[] }) {
    if (!projectId) return;
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await api.updateCompetitor(projectId, editing.id, data);
      } else {
        await api.createCompetitor(projectId, data);
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setSaving(false);
    }
  }

  function requestDelete(c: CompetitorInsight) {
    setPendingDelete(c);
  }

  async function confirmDelete() {
    if (!projectId || !pendingDelete) return;
    setDeleting(true);
    setError('');
    try {
      await api.deleteCompetitor(projectId, pendingDelete.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  async function handleTrackPerformer(row: UntrackedPerformerRow) {
    if (!projectId) return;
    const host = row.domain ? normalizeHost(row.domain) : '';
    if (!host) {
      setEditing(null);
      setDraftCompetitor({ brandName: row.name });
      setModalOpen(true);
      return;
    }
    const key = row.brandId ?? row.name;
    setTrackingBrandKey(key);
    setError('');
    try {
      await api.createCompetitorFromExtracted(projectId, {
        brandName: row.name,
        detectedHost: host
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTrackingBrandKey(null);
    }
  }

  function openAdd() {
    setEditing(null);
    setDraftCompetitor(null);
    setModalOpen(true);
  }

  function findManaged(row: CompetitorIntelligenceRow): CompetitorInsight | null {
    return (
      managedCompetitors.find(competitor => intelligenceRowMatchesCompetitor(row, competitor)) ??
      null
    );
  }

  const rankingRows: CompetitorRankingRow[] = useMemo(
    () =>
      (intelligence?.competitors ?? []).map(row => ({
        ...row,
        managed: findManaged(row)
      })),
    [intelligence, managedCompetitors]
  );

  const rankingColumns = useMemo(
    () => getCompetitorRankingColumns({ onEdit: c => { setEditing(c); setModalOpen(true); }, onDelete: requestDelete }),
    []
  );

  const promptCompetitionColumnDefs = useMemo(
    () => getPromptCompetitionColumns(promptCompetitorColumns),
    [promptCompetitorColumns]
  );

  const providerComparisonColumnDefs = useMemo(
    () => getProviderComparisonColumns(intelligence?.providerComparison ?? []),
    [intelligence]
  );

  const summary = intelligence?.summary;
  const trackedCount = summary?.trackedCompetitors ?? managedCompetitors.length;
  const hasTracked = trackedCount > 0;

  if (loadFailed) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar
            showSources
            actions={
              <Button
                variant="falcon-primary"
                size="sm"
                className="d-flex gap-2 align-items-center justify-content-center text-nowrap flex-shrink-0"
                onClick={openAdd}
              >
                <FontAwesomeIcon icon="plus" />
                افزودن رقیب
              </Button>
            }
          />
        </div>
        <DataLoadError
          onRetry={() => setRetryNonce(n => n + 1)}
          isRetrying={loading}
        />
      </div>
    );
  }

  if (loading && !hasTracked) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar
            showSources
            actions={
              <Button
                variant="falcon-primary"
                size="sm"
                className="d-flex gap-2 align-items-center justify-content-center text-nowrap flex-shrink-0"
                onClick={openAdd}
              >
                <FontAwesomeIcon icon="plus" />
                افزودن رقیب
              </Button>
            }
          />
        </div>
        <CompetitorsPageSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar
          showSources
          actions={
            <Button
              variant="falcon-primary"
              size="sm"
              className="d-flex gap-2 align-items-center justify-content-center text-nowrap flex-shrink-0"
              onClick={openAdd}
            >
              <FontAwesomeIcon icon="plus" />
              افزودن رقیب
            </Button>
          }
        />
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!hasTracked ? (
        <EmptyCompetitorsState onAdd={openAdd} />
      ) : (
        <div className="d-flex flex-column gap-4">
          <Row className="g-3">
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={0}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    رقبای ردیابی‌شده
                    <HelpTooltip html={getHelpHtml('tracked_competitors')} />
                  </span>
                }
                value={trackedCount}
                valueAnnotation="برندهای رقیب ذخیره‌شده"
                valueAnnotationClassName="text-500"
                valueClassName="text-primary"
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={1}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    رقیب برتر AI
                    <HelpTooltip html={getHelpHtml('top_ai_competitor')} />
                  </span>
                }
                value={summary?.topCompetitor?.name ?? '—'}
                valueAnnotation={
                  summary?.topCompetitor
                    ? `${summary.topCompetitor.visibilityScore}% دیده‌شدن در AI`
                    : 'هنوز دیده‌شدن رقیبی نیست'
                }
                valueAnnotationClassName="text-500"
                valueClassName="text-warning"
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={2}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    میانگین دیده‌شدن رقیب در AI
                    <HelpTooltip html={getHelpHtml('avg_competitor_ai_visibility')} />
                  </span>
                }
                value={`${summary?.averageCompetitorVisibility ?? 0}%`}
                valueAnnotation={
                  (intelligence?.competitors.length ?? 0) > 0
                    ? `${intelligence!.competitors.length} رقیب در پاسخ‌های AI`
                    : 'هنوز دیده‌شدن رقیبی نیست'
                }
                valueAnnotationClassName="text-500"
                valueClassName="text-info"
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={3}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    شکاف دیده‌شدن نسبت به شما
                    <HelpTooltip html={getHelpHtml('visibility_gap')} />
                  </span>
                }
                value={formatGap(summary?.visibilityGap)}
                valueAnnotation={
                  summary?.visibilityGap == null
                    ? 'دیده‌شدن شما منهای رقیب برتر'
                    : summary.visibilityGap > 0
                      ? 'شما از رقیب برتر جلوتر هستید'
                      : summary.visibilityGap < 0
                        ? 'شما از رقیب برتر عقب‌تر هستید'
                        : 'مساوی با رقیب برتر'
                }
                valueAnnotationClassName="text-500"
                valueClassName={ownGapTone(summary?.visibilityGap)}
              />
            </Col>
          </Row>

          <Card className="overflow-hidden ag-grid-in-card">
            <FalconCardHeader
              light
              titleTag="h6"
              className="py-3"
              title={
                <div>
                  <span className="d-inline-flex align-items-center gap-1">
                    رتبه‌بندی رقبا
                    <HelpTooltip html={getHelpHtml('competitor_ranking')} />
                  </span>
                  <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                    مرتب‌شده بر اساس امتیاز دیده‌شدن، سپس میانگین رتبه
                  </p>
                </div>
              }
            />
            {(intelligence?.competitors.length ?? 0) === 0 ? (
              <p className="py-5 text-center fs--1 text-400 mb-0">
                رقبا ردیابی شده‌اند، اما هیچ‌کدام در پاسخ‌های AI این بازه ظاهر نشدند.
              </p>
            ) : (
              <Card.Body>
                <div className="for-style">
                  <TypedAgGridWrapper
                    table_name="tracker_ai_competitor_ranking"
                    columns={rankingColumns}
                    data={rankingRows}
                    height="360px"
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      saveGrid: false,
                      resetGrid: false,
                      sideBar: false
                    }}
                  />
                  <style>{`.for-style .ag-row-focus { z-index: 2; }`}</style>
                </div>
              </Card.Body>
            )}
          </Card>

          {projectId && hasTracked && (
            <CompetitorSourceGapTable projectId={projectId} rangeQuery={sourceGapRangeQuery} />
          )}

          <Card className="overflow-hidden ag-grid-in-card">
            <FalconCardHeader
              light
              titleTag="h6"
              className="py-3"
              title={
                <div>
                  <span className="d-inline-flex align-items-center gap-1">
                    جایی که رقبا ظاهر می‌شوند
                    <HelpTooltip html={getHelpHtml('where_competitors_appear')} />
                  </span>
                  <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                    پرامپت‌هایی که رقبای ردیابی‌شده در پاسخ‌های AI ظاهر می‌شوند
                  </p>
                </div>
              }
            />
            {(intelligence?.promptCompetition.length ?? 0) === 0 ? (
              <p className="py-5 text-center fs--1 text-400 mb-0">
                در این بازه منشن رقیب در سطح پرامپت وجود ندارد.
              </p>
            ) : (
              <Card.Body>
                <TypedAgGridWrapper
                  table_name="tracker_ai_competitor_prompt_competition"
                  columns={promptCompetitionColumnDefs}
                  data={intelligence!.promptCompetition}
                  height="420px"
                  otherOptions={{
                    onRowClicked: (event: { data?: { promptId: string } }) => {
                      if (event.data?.promptId) {
                        navigate(toTrackerAi('prompts', `prompt=${event.data.promptId}`));
                      }
                    },
                    rowClass: 'cursor-pointer'
                  }}
                  settings={{
                    pagination: true,
                    fullscreen: false,
                    changeHeight: false,
                    saveGrid: false,
                    resetGrid: false,
                    sideBar: false
                  }}
                />
              </Card.Body>
            )}
          </Card>

          <Row className="g-3">
            <Col lg={untrackedPerformers.length > 0 ? 6 : 12}>
              <Card className="overflow-hidden ag-grid-in-card h-100">
                <FalconCardHeader
                  light
                  titleTag="h6"
                  className="py-3"
                  title={
                    <div>
                      <span className="d-inline-flex align-items-center gap-1">
                        دیده‌شدن رقیب بر اساس مدل AI
                        <HelpTooltip html={getHelpHtml('competitor_by_model')} />
                      </span>
                      <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                        منشن‌ها و رتبه در پرووایدرها
                      </p>
                    </div>
                  }
                />
                {(intelligence?.providerComparison.length ?? 0) === 0 ||
                providerBrandNames.length === 0 ? (
                  <p className="py-5 text-center fs--1 text-400 mb-0">
                    در این بازه داده رقیب در سطح پرووایدر وجود ندارد.
                  </p>
                ) : (
                  <Card.Body>
                    <TypedAgGridWrapper
                      table_name="tracker_ai_competitor_provider_comparison"
                      columns={providerComparisonColumnDefs}
                      data={providerBrandNames}
                      height="300px"
                      settings={{
                        pagination: true,
                        fullscreen: false,
                        changeHeight: false,
                        saveGrid: false,
                        resetGrid: false,
                        sideBar: false
                      }}
                    />
                  </Card.Body>
                )}
              </Card>
            </Col>

            {untrackedPerformers.length > 0 && (
              <Col lg={6}>
                <Card className="border-dashed h-100">
                  <FalconCardHeader
                    light
                    titleTag="h6"
                    className="py-3"
                    title={
                      <div>
                        <span className="d-inline-flex align-items-center gap-1">
                          ظاهر شده اما ردیابی نشده
                          <HelpTooltip html={getHelpHtml('appearing_but_not_tracked')} />
                        </span>
                        <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                          این برندها در پاسخ‌های AI دیده شده‌اند و رتبه خوبی دارند، اما هنوز به
                          لیست رقبای تحت پایش اضافه نشده‌اند.
                        </p>
                      </div>
                    }
                  />
                  <Card.Body>
                    <div className="d-flex flex-column gap-2">
                      {visibleUntrackedPerformers.map((row, index) => {
                        const key = row.brandId ?? row.name;
                        return (
                          <div
                            key={key}
                            className="d-flex flex-wrap align-items-center justify-content-between gap-2 rounded-3 border bg-body-tertiary px-3 py-2"
                          >
                            <div className="d-flex align-items-center gap-2 min-w-0">
                              <BrandFavicon
                                host={row.domain}
                                faviconUrl={row.faviconUrl}
                                name={row.name}
                                colorIndex={index}
                              />
                              <div className="min-w-0">
                                <p className="fs--1 fw-medium text-1000 text-truncate mb-0">
                                  {row.name}
                                </p>
                                <p className="fs--2 text-400 mb-0">
                                  {row.visibilityScore}% دیده‌شدن
                                  {row.avgPosition != null ? ` · رتبه #${row.avgPosition}` : ''}
                                  {summary?.executedPrompts
                                    ? ` · ${row.promptCoverage}/${summary.executedPrompts} پرامپت`
                                    : ''}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="fs--2 py-0 px-2"
                              disabled={trackingBrandKey === key}
                              onClick={() => void handleTrackPerformer(row)}
                            >
                              {trackingBrandKey === key ? '…' : '+ ردیابی'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    {hiddenUntrackedCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowAllUntrackedPerformers(value => !value)}
                        className="btn btn-link btn-sm p-0 mt-3 text-primary d-block ms-auto"
                      >
                        {showAllUntrackedPerformers
                          ? 'نمایش کمتر'
                          : `بیشتر (${hiddenUntrackedCount})`}
                      </button>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {notAppearingCompetitors.length > 0 && (
            <Card className="border-dashed">
              <FalconCardHeader
                light
                titleTag="h6"
                className="py-3"
                title={
                  <div>
                    <span className="d-inline-flex align-items-center gap-1">
                      ردیابی شده اما ظاهر نشده
                      <HelpTooltip html={getHelpHtml('tracked_but_not_appearing')} />
                    </span>
                    <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                      این رقبا ذخیره شده‌اند اما در پاسخ‌های AI این بازه شناسایی نشدند.
                    </p>
                  </div>
                }
              />
              <Card.Body>
                <div className="d-flex flex-wrap gap-2">
                  {notAppearingCompetitors.map(c => (
                    <span
                      key={c.id}
                      className="d-inline-flex align-items-center gap-2 rounded-pill border bg-body-tertiary px-3 py-1 fs--2 text-600"
                    >
                      <BrandFavicon host={c.host} name={c.brandName} size="xs" />
                      {c.brandName}
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(c);
                          setModalOpen(true);
                        }}
                        className="btn btn-link btn-sm p-0 text-primary"
                      >
                        ویرایش
                      </button>
                    </span>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      )}

      <CompetitorModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          setDraftCompetitor(null);
        }}
        initial={
          editing
            ? {
                brandName: editing.brandName,
                websiteUrl: editing.websiteUrl,
                aliases: editing.aliases.map(a => a.name)
              }
            : draftCompetitor
              ? {
                  brandName: draftCompetitor.brandName,
                  websiteUrl: '',
                  aliases: []
                }
              : undefined
        }
        title={editing ? 'ویرایش رقیب' : 'افزودن رقیب'}
        onSubmit={handleSave}
        saving={saving}
      />

      <CancelModal
        show={pendingDelete != null}
        setShow={() => setPendingDelete(null)}
        title="حذف رقیب"
        submitTitle="آره، حذف بشه"
        isSubmitting={deleting}
        submitFunc={confirmDelete}
        desc={
          pendingDelete
            ? `آیا مطمئن هستید می‌خواهید رقیب «${pendingDelete.brandName}» حذف شود؟ این عمل قابل بازگشت نیست.`
            : ''
        }
      />
    </div>
  );
}

function EmptyCompetitorsState({ onAdd }: { onAdd: () => void }) {
  return (
    <Card className="border-dashed">
      <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
        <p className="fs-0 fw-medium text-1000 mb-1">هنوز رقیبی ردیابی نشده</p>
        <p className="fs--1 text-500 mb-0" style={{ maxWidth: '28rem' }}>
          برندهای رقیب را اضافه کنید تا دیده‌شدن در AI، رتبه‌بندی و رقابت پرامپت را مقایسه کنید.
        </p>
        <Button
          size="sm"
          variant="falcon-primary"
          className="mt-3 d-flex gap-2 align-items-center justify-content-center text-nowrap"
          onClick={onAdd}
        >
          <FontAwesomeIcon icon="plus" />
          افزودن رقیب
        </Button>
      </Card.Body>
    </Card>
  );
}

function formatGap(gap: number | null | undefined): string {
  if (gap == null) return '—';
  const sign = gap > 0 ? '+' : '';
  return `${sign}${gap}%`;
}

/** Summary gap = own visibility − top competitor (positive = you lead). */
function ownGapTone(gap: number | null | undefined): string {
  if (gap == null) return 'text-400';
  if (gap > 0) return 'text-success';
  if (gap < 0) return 'text-warning';
  return 'text-500';
}

export default CompetitorsPage;
